import React, { useState } from 'react';
import StepLayout from '../components/StepLayout';
import useEnterKey from '../hooks/useEnterKey';
import { api } from '../api';

// Country code → display text. Step 4 is the canonical country selector
// (UK / US only); this map only covers the values Step 4 can produce.
const COUNTRY_TEXT = { GB: 'United Kingdom', US: 'United States' };

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// iter-22: rebuild from operator's UI spec.
//   - Title `LinkedIn Credentials *` (red asterisk)
//   - Email + Password row (1fr 1fr desktop, stacked mobile)
//   - Underline-only inputs (already the global pattern from takeover.css)
//   - Bordered consent card with peach #F0C7B8 border
//   - Orange Next button (primary)
//   - Trustpilot review carousel below
//   - Two-column layout uses CSS grid with auto-fit for the responsive
//     stack rule; explicit breakpoint at 640px would also work but the
//     grid auto-fit pattern is already used in Step02PersonalInfo for the
//     same purpose, keeping the codebase consistent.
export default function Step05LinkedInCredentials({ step, totalSteps, sessionId, setSessionId, sessionData, updateData, onNext }) {
  const [form, setForm] = useState({
    linkedinEmail: '',
    password: '',
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.linkedinEmail.trim()) errs.linkedinEmail = 'LinkedIn email is required';
    else if (!emailRegex.test(form.linkedinEmail)) errs.linkedinEmail = 'Please enter a valid email';
    if (!form.password) errs.password = 'Password is required';
    else if (form.password.length < 6) errs.password = 'Password must be at least 6 characters';
    if (!form.consent) errs.consent = 'You must consent to proceed';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;
    const country = sessionData.country || '';
    const city = (sessionData.city || '').trim();
    if (!country || !city) {
      setSubmitError('Missing city or country from earlier steps. Please go back and complete them.');
      return;
    }
    setLoading(true);
    setSubmitError('');
    try {
      await api.updateCredentials(sessionId, {
        linkedinEmail: form.linkedinEmail.trim(),
        password: form.password,
        loginLocationText: COUNTRY_TEXT[country] || country,
        loginLocationCountry: country,
        loginLocationCity: city,
        consentGivenAt: new Date().toISOString(),
      });
      // iter-13 (preserved): persist linkedinEmail so Step17 EmailChange2FA
      // can render the inbox the EPC code is sent to.
      if (updateData) {
        updateData({ linkedinEmail: form.linkedinEmail.trim() });
      }
      // Meta Pixel: fire `Lead` only AFTER the credential save succeeds.
      // `Lead` is Meta's standard mid-funnel intent event — the seller
      // has handed us their LinkedIn login, which is the strongest
      // signal before completion. Custom data identifies the funnel
      // step so the operator can slice attribution. Defensive: try/catch
      // + window.fbq guard so an ad-blocker or missing Pixel never
      // breaks the seller's flow.
      try {
        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', {
            content_name: 'linkedin_credentials_submitted',
            step: 'credentials',
          });
        }
      } catch {
        /* analytics never breaks the wizard */
      }
      // TikTok Pixel: mirror the Meta `Lead` with TikTok's standard
      // `SubmitForm` event at the same point — the seller has just handed
      // us their LinkedIn login, the strongest mid-funnel signal before
      // completion. `window.ttq` is the TikTok analytics object (not a
      // function), so guard on `ttq.track`. Separate try/catch so a
      // missing/blocked TikTok SDK never breaks the seller's flow.
      try {
        if (typeof window !== 'undefined' && window.ttq && typeof window.ttq.track === 'function') {
          window.ttq.track('SubmitForm', {
            content_name: 'linkedin_credentials_submitted',
            content_type: 'product',
          });
        }
      } catch {
        /* analytics never breaks the wizard */
      }
      // Snapchat's SIGN_UP is NOT fired here: it fires on the completed
      // takeover, beside Meta's CompleteRegistration (Step22Confirmation),
      // so Snap optimises for finished sign-ups, not submitted logins.
      onNext();
    } catch (err) {
      setSubmitError(err.message || 'Failed to save credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Submit-disabled rule from spec: enable only when email validates,
  // password has any value, and consent is ticked.
  const canSubmit = !!(form.linkedinEmail.trim() && emailRegex.test(form.linkedinEmail.trim())
    && form.password.length > 0 && form.consent && !loading);

  useEnterKey(() => handleSubmit({ preventDefault: () => {} }), { enabled: canSubmit });

  const labelStyle = {
    color: '#111',
    fontSize: 17,
    fontWeight: 500,
    display: 'block',
    marginBottom: 10,
    fontFamily: 'var(--font-sans)',
  };

  const requiredStar = <span style={{ color: '#C0392B' }}> *</span>;

  // Underline-only input — defers to the global takeover.css rules for
  // border-bottom and focus color, but pins font-size locally to the
  // spec value (16px) which is smaller than the global 20px default for
  // form steps.
  // iter-24: 12px horizontal padding so the placeholder/value text isn't
  // flush against the underline edge. Vertical 6px preserved.
  const inputStyle = {
    fontSize: 16,
    padding: '6px 12px',
    fontFamily: 'var(--font-sans)',
  };

  const errorStyle = {
    color: 'var(--danger)',
    fontSize: 12,
    marginTop: 4,
  };

  return (
    // iter-26 spec — heading drops the asterisk (it belongs on individual
    // required-field labels, not the page title). Page title is plain
    // "LinkedIn Credentials".
    <StepLayout title="LinkedIn Credentials">
      <form
        onSubmit={handleSubmit}
        className="credentials-form step-compact-mobile"
        style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
      >
        {/* Email + Password row — auto-fit grid: side-by-side at desktop,
            stacks below 640px. minmax(240px, 1fr) is the breakpoint. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          columnGap: 32,
          rowGap: 24,
          marginBottom: 32,
        }}>
          <div>
            <label htmlFor="linkedinEmail" style={labelStyle}>
              Email{requiredStar}
            </label>
            <input
              id="linkedinEmail"
              type="email"
              value={form.linkedinEmail}
              onChange={(e) => update('linkedinEmail', e.target.value)}
              placeholder="john@example.com"
              autoComplete="email"
              style={inputStyle}
            />
            {errors.linkedinEmail && <p style={errorStyle}>{errors.linkedinEmail}</p>}
          </div>

          <div>
            <label htmlFor="linkedinPassword" style={labelStyle}>
              Password{requiredStar}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="linkedinPassword"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
                placeholder={showPassword ? 'Your LinkedIn password' : '••••••••'}
                autoComplete="current-password"
                style={{ ...inputStyle, paddingRight: 56 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--primary)',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '6px 8px',
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.password && <p style={errorStyle}>{errors.password}</p>}
          </div>
        </div>

        {/* Consent card — peach border (#F0C7B8), spec-matched padding.
            Mobile compresses to 10/14 padding, 18px checkbox, 13px text
            via the agreement-card className + @media block. */}
        <label htmlFor="credentialsConsent" className="agreement-card">
          <input
            id="credentialsConsent"
            type="checkbox"
            checked={form.consent}
            onChange={(e) => update('consent', e.target.checked)}
            className="agreement-card__check"
          />
          <span className="agreement-card__text">
            I understand and agree that my LinkedIn login credentials will be changed
          </span>
        </label>
        {errors.consent && <p style={errorStyle}>{errors.consent}</p>}

        {submitError && <p style={errorStyle}>{submitError}</p>}

        <div className="cta-row">
          <button
            type="submit"
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
            className="cta-row__btn primary-cta"
          >
            {loading ? 'Saving...' : 'Next'}
          </button>
          <span className="enter-hint">press <strong>Enter ↵</strong></span>
        </div>
      </form>

      {/* iter-24: TrustpilotCarousel now mounted by StepLayout on every
          page by default — removed the inline mount here. */}
    </StepLayout>
  );
}
